import React, {useCallback, useEffect, useRef, useState, useId} from 'react'
import {createPortal} from 'react-dom'
import CheckIcon from '../../images/svg/CheckIcon'
import ErrorIcon from '../../images/svg/ErrorIcon'
import InfoIcon from '../../images/svg/InfoIcon'
import WarningIcon from '../../images/svg/WarningIcon'
import XIcon from '../../images/svg/XIcon'

function getVariantIcon(variant: ToastProps['variant'], className: string) {
  switch (variant) {
    case 'success':
      return <CheckIcon className={className} />
    case 'error':
      return <ErrorIcon className={className} />
    case 'warning':
      return <WarningIcon className={className} />
    case 'info':
      return <InfoIcon className={className} />
    default:
      return null
  }
}

function getOverlayClasses(mode: ToastProps['overlayMode']) {
  switch (mode) {
    case 'dim':
      return {wrapper: '', backdrop: 'bg-black/40 dark:bg-black/60'}
    case 'blur':
      return {wrapper: 'backdrop-blur-sm', backdrop: ''}
    case 'dim+blur':
      return {wrapper: 'backdrop-blur-sm', backdrop: 'bg-black/40 dark:bg-black/60'}
    default:
      return {wrapper: '', backdrop: ''}
  }
}

function getColors(variant: ToastProps['variant']) {
  switch (variant) {
    case 'success':
      return {
        bg: 'bg-white dark:bg-slate-900',
        border: 'border-green-200 dark:border-green-500/40',
        iconWrap: 'bg-green-100 dark:bg-green-900/40',
        icon: 'text-green-700 dark:text-green-300',
        title: 'text-slate-900 dark:text-slate-100',
        text: 'text-slate-600 dark:text-slate-300',
        progress: 'bg-green-500 dark:bg-green-400',
        ring: 'focus:ring-green-500 focus:ring-offset-white dark:focus:ring-offset-slate-900',
      }
    case 'error':
      return {
        bg: 'bg-white dark:bg-slate-900',
        border: 'border-rose-200 dark:border-rose-500/40',
        iconWrap: 'bg-rose-100 dark:bg-rose-900/40',
        icon: 'text-rose-700 dark:text-rose-300',
        title: 'text-slate-900 dark:text-slate-100',
        text: 'text-slate-600 dark:text-slate-300',
        progress: 'bg-rose-500 dark:bg-rose-400',
        ring: 'focus:ring-rose-500 focus:ring-offset-white dark:focus:ring-offset-slate-900',
      }
    case 'warning':
      return {
        bg: 'bg-white dark:bg-slate-900',
        border: 'border-amber-200 dark:border-amber-500/40',
        iconWrap: 'bg-amber-100 dark:bg-amber-900/40',
        icon: 'text-amber-700 dark:text-amber-300',
        title: 'text-slate-900 dark:text-slate-100',
        text: 'text-slate-700 dark:text-slate-300',
        progress: 'bg-amber-500 dark:bg-amber-400',
        ring: 'focus:ring-amber-500 focus:ring-offset-white dark:focus:ring-offset-slate-900',
      }
    default:
      return {
        bg: 'bg-white dark:bg-slate-900',
        border: 'border-indigo-200 dark:border-indigo-600',
        iconWrap: 'bg-indigo-100 dark:bg-indigo-800',
        icon: 'text-indigo-700 dark:text-indigo-300',
        title: 'text-slate-900 dark:text-slate-100',
        text: 'text-slate-600 dark:text-slate-300',
        progress: 'bg-indigo-500 dark:bg-indigo-400',
        ring: 'focus:ring-indigo-500 focus:ring-offset-white dark:focus:ring-offset-slate-900',
      }
  }
}

export interface ToastProps {
  open: boolean
  onOpenChange?: (open: boolean) => void
  title?: string
  description?: React.ReactNode | string
  variant?: 'success' | 'error' | 'warning' | 'info'
  duration?: number
  overlayMode?: 'none' | 'dim' | 'blur' | 'dim+blur'
  dismissible?: boolean
  overlayClickable?: boolean
  className?: string
  closeLabel?: string
  autoDismiss?: boolean
  children?: React.ReactNode
}

export default function Toast(props: ToastProps) {
  const {
    open,
    onOpenChange,
    title = '',
    description,
    variant = 'info',
    duration = 4000,
    overlayMode = 'none',
    dismissible = true,
    overlayClickable = true,
    className = '',
    closeLabel = 'Close',
    autoDismiss = false,
    children = null,
  } = props

  const [exiting, setExiting] = useState(false)
  const [progressStarted, setProgressStarted] = useState(false)
  const autoTimerRef = useRef<number | null>(null)
  const exitTimerRef = useRef<number | null>(null)

  const titleId = useId()
  const descId = useId()

  const beginClose = useCallback(() => {
    if (!open) return
    setExiting(true)
    exitTimerRef.current = window.setTimeout(() => {
      onOpenChange?.(false)
      setExiting(false)
      setProgressStarted(false)
    }, 220)
  }, [open, onOpenChange])

  useEffect(() => {
    if (!open) return

    const start = window.setTimeout(() => setProgressStarted(true), 20)
    if (autoDismiss) {
      autoTimerRef.current = window.setTimeout(() => beginClose(), duration)
      return () => {
        window.clearTimeout(start)
        if (autoTimerRef.current) window.clearTimeout(autoTimerRef.current)
      }
    }

    return () => {
      window.clearTimeout(start)
    }
  }, [open, autoDismiss, duration, beginClose])

  useEffect(
    () => () => {
      if (exitTimerRef.current) window.clearTimeout(exitTimerRef.current)
    },
    []
  )

  if (!open && !exiting) return null

  const colors = getColors(variant)
  const overlay = getOverlayClasses(overlayMode)
  const _duration = autoDismiss ? duration : 500

  const body = (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={description ? descId : undefined}
      className={`fixed inset-0 z-[9999] flex items-center justify-center ${overlay.wrapper}`}
      onClick={overlayClickable ? beginClose : undefined}
    >
      {overlayMode !== 'none' && (
        <div
          className={`${overlay.backdrop} absolute inset-0`}
          aria-hidden="true"
        />
      )}

      <div
        className={[
          'w-full max-h-[90vh] overflow-y-auto max-w-md',
          'relative mx-4 shadow-2xl rounded-lg',
          colors.border,
          colors.bg,
          'transition-all duration-200',
          exiting ? 'opacity-0 scale-95' : 'opacity-100 scale-100',
          'focus:outline-none',
          className,
        ].join(' ')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rounded-lg p-3">
          <div className="flex">
            <div className="flex items-start">
              <span
                className={[
                  'mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                  colors.iconWrap,
                ].join(' ')}
              >
                {getVariantIcon(variant, ['h-5 w-5', colors.icon].join(' '))}
              </span>
            </div>

            <div className="flex flex-1 items-start pl-2 pt-0.5">
              {title && (
                <h3
                  id={titleId}
                  className={['text-base font-semibold', colors.title].join(' ')}
                >
                  {title}
                </h3>
              )}
            </div>

            <div className="flex items-start">
              {dismissible && (
                <button
                  type="button"
                  onClick={beginClose}
                  aria-label={closeLabel}
                  className={[
                    'ml-2 inline-flex h-7 w-7 items-center justify-center rounded-full',
                    'transition hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2',
                    colors.ring,
                  ].join(' ')}
                >
                  <XIcon className={['h-5 w-5', colors.icon].join(' ')} />
                </button>
              )}
            </div>
          </div>

          {description && (
            <div
              id={descId}
              className={['text-sm leading-5 p-2', colors.text].join(' ')}
            >
              {description}
            </div>
          )}
        </div>

        {children}

        <div className="pointer-events-none relative h-1 overflow-hidden rounded-b-lg">
          <div className="absolute inset-0 flex justify-center">
            <div
              id="progress-bar"
              className={['h-full rounded-b-lg', colors.progress].join(' ')}
              style={{
                width: progressStarted ? '100%' : '0%',
                transition: `width ${Math.max(0, _duration - 150)}ms linear`,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(body, document.body)
}
